import { useState, useRef, useEffect, useCallback } from "react";
import { useLanguage } from "@/hooks/useLanguage";
import { Globe, ChevronDown, Check } from "lucide-react";
import type { SupportedLanguage } from "@/config/i18n";

/**
 * 语言切换组件 - Vercel 风格
 * 提供一个下拉菜单用于切换语言
 * 根据组件在页面中的位置自动决定菜单向上或向下打开
 */
export function LanguageSwitcher() {
  const {
    currentLanguage,
    currentLanguageConfig,
    changeLanguage,
    supportedLanguages,
    getLanguageConfig,
  } = useLanguage();

  const [isOpen, setIsOpen] = useState(false);
  const [openDirection, setOpenDirection] = useState<"up" | "down">("up");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 计算菜单打开方向
  const calculateOpenDirection = useCallback(() => {
    if (!dropdownRef.current) return;

    const rect = dropdownRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    // 预估菜单高度（每个选项约 40px，加上 padding）
    const estimatedMenuHeight = supportedLanguages.length * 40 + 16;

    // 如果组件距离视口底部的空间大于菜单高度，向下打开
    // 否则向上打开
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow >= estimatedMenuHeight) {
      setOpenDirection("down");
    } else if (spaceAbove >= estimatedMenuHeight) {
      setOpenDirection("up");
    } else {
      // 如果上下空间都不够，选择空间更大的方向
      setOpenDirection(spaceBelow >= spaceAbove ? "down" : "up");
    }
  }, [supportedLanguages.length]);

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (lang: SupportedLanguage) => {
    changeLanguage(lang);
    setIsOpen(false);
  };

  // 打开菜单时计算方向
  const handleToggle = () => {
    if (!isOpen) {
      calculateOpenDirection();
    }
    setIsOpen(!isOpen);
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      {/* 触发按钮 - Vercel 风格 */}
      <button
        type="button"
        onClick={handleToggle}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          height: "32px",
          padding: "0 12px",
          fontSize: "13px",
          fontWeight: 400,
          color: "rgb(var(--vercel-fg-secondary))",
          backgroundColor: "transparent",
          border: "1px solid rgb(var(--vercel-border-primary))",
          borderRadius: "6px",
          cursor: "pointer",
          transition: "all 150ms ease",
          outline: "none",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "rgb(var(--vercel-fg-tertiary))";
          e.currentTarget.style.color = "rgb(var(--vercel-fg-primary))";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor =
            "rgb(var(--vercel-border-primary))";
          e.currentTarget.style.color = "rgb(var(--vercel-fg-secondary))";
        }}
      >
        <Globe
          className="hidden sm:block"
          style={{ width: "14px", height: "14px" }}
        />
        <span className="hidden sm:inline">{currentLanguageConfig.label}</span>
        <span className="sm:hidden">{currentLanguageConfig.flag}</span>
        <ChevronDown
          style={{
            width: "12px",
            height: "12px",
            marginLeft: "2px",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 150ms ease",
          }}
        />
      </button>

      {/* 下拉菜单 - 根据位置自动向上或向下打开 */}
      {isOpen && (
        <div
          className={`absolute right-0 min-w-[140px] rounded-lg shadow-lg z-[9999] overflow-hidden bg-popover border border-border ${
            openDirection === "up"
              ? "bottom-[calc(100%+4px)]"
              : "top-[calc(100%+4px)]"
          }`}
        >
          <div className="p-1">
            {supportedLanguages.map((lang) => {
              const config = getLanguageConfig(lang);
              const isActive = currentLanguage === lang;

              return (
                <button
                  key={lang}
                  type="button"
                  onClick={() => handleSelect(lang as SupportedLanguage)}
                  className={`flex items-center gap-2 w-full px-3 py-2 text-[13px] rounded-md cursor-pointer text-left transition-colors duration-150 ${
                    isActive
                      ? "font-medium text-foreground bg-accent"
                      : "font-normal text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                >
                  <span className="text-sm">{config.flag}</span>
                  <span className="flex-1">{config.label}</span>
                  {isActive && (
                    <Check className="w-3.5 h-3.5 text-foreground" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
